import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { ActionIcon, Box, Menu, ScrollAreaAutosize, useMantineColorScheme } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { ListTableColumnFiltered } from "../ListTable";
import { getRenderDraggable } from "../ListTable/components/ColumnsOrganizer/utils";

import { IconFilterTable } from "@/components/icons";
import Popover from "@/components/widgets/Popover/Popover";

import type { FC } from "react";

interface PopoverTableProps {
  popoverOpened: boolean;
  setPopoverOpened: React.Dispatch<React.SetStateAction<boolean>>;
  actionInPending: boolean;
  filteredColumns: ListTableColumnFiltered[];
  onFilteredColumnsChange: (columns: ListTableColumnFiltered[]) => void;
  title?: string;
}

const PopoverTable: FC<PopoverTableProps> = ({
  popoverOpened,
  setPopoverOpened,
  actionInPending,
  filteredColumns,
  onFilteredColumnsChange,
  title,
}) => {
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const defaultTitle = t("common.customizeColumns");

  /* Handlers */
  const getToggleColumn = (name: string) => () => {
    onFilteredColumnsChange(
      filteredColumns.map((item) => (item.id === name ? { ...item, active: !item.active } : item))
    );
  };

  return (
    <Popover
      title={title || defaultTitle}
      popoverOpened={popoverOpened}
      setPopoverOpened={setPopoverOpened}
      actionInPending={actionInPending}
      target={
        <ActionIcon
          variant="light"
          size="input-sm"
          onClick={() => setPopoverOpened((prev) => !prev)}
          style={{
            backgroundColor: isDark ? "var(--mantine-color-primaryDark-7)" : undefined,
          }}
        >
          <IconFilterTable />
        </ActionIcon>
      }
    >
      <Menu key={filteredColumns.map((c) => c.active).join("-")}>
        <DragDropContext
          onDragEnd={({ source, destination }) => {
            if (!destination) return;
            const newColumns = [...filteredColumns];
            const [removed] = newColumns.splice(source.index, 1);
            newColumns.splice(destination.index, 0, removed);
            onFilteredColumnsChange(newColumns);
          }}
        >
          <Droppable
            droppableId="columns-organizer"
            direction="vertical"
            renderClone={getRenderDraggable(filteredColumns, getToggleColumn)}
          >
            {({ droppableProps, innerRef, placeholder }) => (
              <ScrollAreaAutosize mah={400} offsetScrollbars scrollbarSize={4} type="auto">
                <Box {...droppableProps} ref={innerRef}>
                  {filteredColumns.map((col, index) => (
                    <Draggable key={col.id} index={index} draggableId={col.id}>
                      {getRenderDraggable(filteredColumns, getToggleColumn)}
                    </Draggable>
                  ))}
                  {placeholder}
                </Box>
              </ScrollAreaAutosize>
            )}
          </Droppable>
        </DragDropContext>
      </Menu>
    </Popover>
  );
};

export default PopoverTable;
